<script setup lang="ts">
import { filename } from "pathe/utils";
import type { ReviewCompany } from "@/types/Ranking";

const props = defineProps<{
  company: ReviewCompany;
}>();

// 1〜3位は3件、4〜5位は1件を初期表示
const defaultCount = computed(() => (props.company.rank <= 3 ? 3 : 1));

const expanded = ref(false);

const visibleReviews = computed(() =>
  expanded.value
    ? props.company.reviews
    : props.company.reviews.slice(0, defaultCount.value)
);

const hasMore = computed(
  () => props.company.reviews.length > defaultCount.value
);

// レビューアバター（icon_man_*.png / icon_woman_*.png）
const manGlob = import.meta.glob<{ default: string }>("~/assets/images/icon_man_*.png", {
  eager: true,
});
const womanGlob = import.meta.glob<{ default: string }>("~/assets/images/icon_woman_*.png", {
  eager: true,
});
const avatars = Object.fromEntries(
  [...Object.entries(manGlob), ...Object.entries(womanGlob)].map(
    ([key, value]) => [filename(key), value.default]
  )
);
const avatarSrc = (key: string) => avatars[key];

const toggle = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <div class="reviewCard">
    <div :class="['reviewCard-head', `reviewCard-head--${company.rank}`]">
      <AtomsRankingMedal :rank="company.rank" context="review" />
      <span class="reviewCard-company">{{ company.name }}</span>
    </div>

    <div class="reviewCard-reviews">
      <div
        v-for="(review, idx) in visibleReviews"
        :key="idx"
        class="review"
      >
        <div class="review-profile">
          <span
            class="review-avatar"
            :class="`review-avatar--${review.gender}`"
          >
            <img :src="avatarSrc(review.avatar)" alt="" />
          </span>
          <span class="review-attribute">{{ review.attribute }}</span>
        </div>
        <p class="review-body">{{ review.body }}</p>
      </div>
    </div>

    <button
      v-if="hasMore"
      type="button"
      class="reviewCard-readmore"
      :class="{ 'is-open': expanded }"
      :aria-expanded="expanded"
      @click="toggle"
    >
      <span class="reviewCard-readmore-text">{{
        expanded ? "口コミを閉じる" : "その他の口コミを読む"
      }}</span>
      <span class="reviewCard-readmore-arrow" aria-hidden="true" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.reviewCard {
  background-color: #fff;
  padding-bottom: 24px;
}

/* 会社ヘッダ */
.reviewCard-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 27px 15px;
  min-height: 80px;
  box-sizing: border-box;
}

.reviewCard-head--1 {
  background: linear-gradient(0deg, #ffffff 50%, #fff1d3 100%);
}

.reviewCard-head--2 {
  background: linear-gradient(0deg, #ffffff 50%, #E8F1F4 100%);
}

.reviewCard-head--3 {
  background: linear-gradient(0deg, #ffffff 50%, #FFEFE9 100%);
}

.reviewCard-company {
  font-size: px(15);
  font-weight: 700;
  line-height: 24px;
  color: $colorBasicText;
  flex: 1;
}

/* レビュー1件 */
.review {
  padding: 8px 15px 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 15px;
    right: 15px;
    height: 1px;
    background-color: $colorGray;
  }
}

.review-profile {
  display: flex;
  align-items: center;
  gap: 4px;
}

.review-avatar {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &--male {
    background-color: $colorLightBlue;
  }
  &--female {
    background-color: #fff3f2;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.review-attribute {
  font-size: px(12);
  line-height: 1.25;
  color: $colorCompareGray;
}

.review-body {
  margin-top: 8px;
  font-size: px(14);
  line-height: 20px;
  color: $colorBasicText;
}

/* もっと見る（SP: テキストリンク） */
.reviewCard-readmore {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 40px;
  background: none;
}

.reviewCard-readmore-text {
  font-size: px(15);
  color: $colorDarkBlue;
}

.reviewCard-readmore-arrow {
  width: 9px;
  height: 9px;
  border-right: 2px solid $colorDarkBlue;
  border-bottom: 2px solid $colorDarkBlue;
  transform: translateY(-3px) rotate(45deg);
  transition: transform 0.3s;
  flex: none;
}

.reviewCard-readmore.is-open .reviewCard-readmore-arrow {
  transform: translateY(2px) rotate(225deg);
}

@include media(pc) {
  .reviewCard {
    padding-bottom: 32px;
  }

  .reviewCard-head {
    padding: 29px 24px 24px;
    gap: 16px;
  }

  .reviewCard-company {
    font-size: px(22);
    line-height: 32px;
  }

  /* PC: アバター+属性=左 / 本文=右 の2カラム */
  .review {
    display: flex;
    align-items: flex-start;
    padding: 0 24px;

    &::before {
      left: 24px;
      right: 24px;
    }

    & + .review {
      border-top: none;
    }
  }

  .review-profile {
    flex: none;
    width: 135px;
    padding: 16px 0;
  }

  .review-body {
    margin-top: 0;
    flex: 1;
    padding: 16px 0 16px 16px;
  }

  /* もっと見る（PC: グレーボックスボタン） */
  .reviewCard-readmore {
    margin: 0 auto;
    max-width: 320px;
    border-radius: 4px;
    background-color: #efefef;
    box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  }

  .reviewCard-readmore-text {
    color: $colorBlue;
  }

  .reviewCard-readmore-arrow {
    order: -1;
    border-right-color: $colorBlue;
    border-bottom-color: $colorBlue;
  }
}
</style>
